<template>
  <v-row no-gutters>
    <v-col class="video-page-settings pa-0">
      <div class="d-flex flex-row align-center">
        <h3 class="body-label-caption py-0 px-3 mt-10">
          {{ $t('message.pages.video.playerOptionTitles.pandaIa') }}
        </h3>
        <credits-info
          style="display: none"
          :activationPrice="aiWorkFlowCreditsBalance.aiWorkFlowPrice"
        ></credits-info>
      </div>
      <v-card flat tile class="d-flex flex-column text-left">
        <!-- assist ai-->
        <v-btn
          v-if="!video.hasOwnProperty('video_source')"
          class="settings-btn py-0 px-3"
          x-large
          @click="handleSelectOption('assistAi')"
          text
        >
          <span class="d-flex">
            <p class="settings-btn--text">
              Panda Assist AI
              <!-- <sup class="newItemText">BETA</sup> -->
              <base-chip class="new-item--chip" size="small"
                ><span class="body-bold-smaller brand-20-darkest--text">{{
                  $i18next.t('message.newItemChip')
                }}</span></base-chip
              >
            </p>
          </span>
          <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
        </v-btn>
      </v-card>
      <v-row no-gutters class="d-flex mt-6 mx-3" v-if="hasError">
        <v-col>
          <base-alert
            v-model="hasError"
            type="danger"
            :title="$t('message.ai.queueError')"
            :description="$t('message.ai.queueErrorDescription')"
          >
            <!-- <v-spacer></v-spacer> -->
            <v-row no-gutters>
              <base-btn icon size="small" type="secondary" class="mx-3"
                >{{ $t('message.knowMore')
                }}<v-icon class="body-bold-smaller pl-2" size="20"
                  >mdi-open-in-new</v-icon
                ></base-btn
              >
              <v-icon @click="handleCloseErrorAlert" size="16"
                >panda-close</v-icon
              >
            </v-row>
          </base-alert>
        </v-col>
      </v-row>
      <v-card
        v-else-if="aiWorkFlowCreated"
        flat
        tile
        class="d-flex flex-column text-left"
      >
        <!-- mind map -->
        <v-btn
          v-if="!video.hasOwnProperty('video_source')"
          id="mindmap"
          class="settings-btn py-0 px-3"
          :class="{ 'settings-btn--disabled': isLoading.mindmap }"
          x-large
          @click="handleSelectOption('mindMap')"
          text
        >
          <span class="d-flex">
            <p
              class="settings-btn--text"
              :class="{ 'disabled-text-color': isLoading.mindmap }"
            >
              {{ $t('message.ai.mindmap.mindmap') }}
            </p>
          </span>
          <v-row no-gutters justify="end" v-if="isLoading.mindmap && !hasError">
            <v-col cols="1" align-self="center">
              <v-row no-gutters justify="end">
                <base-progress-circular></base-progress-circular>
              </v-row>
            </v-col>
            <base-chip class="settings-btn--loading-chip mx-2" size="small">{{
              $t('message.ai.activatingPandaAi')
            }}</base-chip>
          </v-row>
          <v-row
            no-gutters
            justify="end"
            v-else-if="isItemEnabledOnPlayer.mindmap"
          >
            <v-spacer></v-spacer>
            <span class="d-flex enabled-setting-icon">
              <v-icon size="16">panda-check-round</v-icon>
            </span>
          </v-row>
          <v-icon
            class="settings-btn--icon"
            :class="{ 'disabled-text-color': isLoading.mindmap }"
            size="16"
            >panda-go</v-icon
          >
        </v-btn>
        <!-- quiz -->
        <v-btn
          v-if="!video.hasOwnProperty('video_source')"
          id="quiz"
          class="settings-btn py-0 px-3"
          :class="{ 'settings-btn--disabled': isLoading.questions }"
          x-large
          @click="handleSelectOption('quiz')"
          text
        >
          <span class="d-flex">
            <p
              class="settings-btn--text"
              :class="{ 'disabled-text-color': isLoading.questions }"
            >
              Quiz
            </p>
          </span>
          <v-row
            no-gutters
            justify="end"
            v-if="isLoading.questions && !hasError"
          >
            <v-col cols="1" align-self="center">
              <v-row no-gutters justify="end">
                <base-progress-circular></base-progress-circular>
              </v-row>
            </v-col>
            <base-chip class="settings-btn--loading-chip mx-2" size="small">{{
              $t('message.ai.activatingPandaAi')
            }}</base-chip>
          </v-row>
          <v-row
            no-gutters
            justify="end"
            v-else-if="isItemEnabledOnPlayer.questions"
          >
            <v-spacer></v-spacer>
            <span class="d-flex enabled-setting-icon">
              <v-icon size="16">panda-check-round</v-icon>
            </span>
          </v-row>
          <v-icon
            class="settings-btn--icon"
            :class="{ 'disabled-text-color': isLoading.questions }"
            size="16"
            >panda-go</v-icon
          >
        </v-btn>
        <!-- ebook -->
        <v-btn
          v-if="!video.hasOwnProperty('video_source')"
          id="ebook"
          class="settings-btn py-0 px-3"
          :class="{ 'settings-btn--disabled': isLoading.abstract }"
          x-large
          @click="handleSelectOption('ebook')"
          text
        >
          <span class="d-flex">
            <p
              class="settings-btn--text"
              :class="{ 'disabled-text-color': isLoading.abstract }"
            >
              {{ $i18next.t('message.ai.abstract.ebook') }}
            </p>
          </span>
          <v-row
            no-gutters
            justify="end"
            v-if="isLoading.abstract && !hasError"
          >
            <v-col cols="1" align-self="center">
              <v-row no-gutters justify="end">
                <base-progress-circular></base-progress-circular>
              </v-row>
            </v-col>
            <base-chip class="settings-btn--loading-chip mx-2" size="small">{{
              $t('message.ai.activatingPandaAi')
            }}</base-chip>
          </v-row>
          <v-row
            no-gutters
            justify="end"
            v-else-if="isItemEnabledOnPlayer.abstract"
          >
            <v-spacer></v-spacer>
            <span class="d-flex enabled-setting-icon">
              <v-icon size="16">panda-check-round</v-icon>
            </span>
          </v-row>
          <v-icon
            class="settings-btn--icon"
            :class="{ 'disabled-text-color': isLoading.abstract }"
            size="16"
            >panda-go</v-icon
          >
        </v-btn>
      </v-card>
      <v-sheet
        v-else-if="canSeePlanInfo"
        class="d-flex activation-card mx-3"
        height="auto"
        width="auto"
      >
        <v-row no-gutters :justify="isMobileBreakpoint ? 'start' : 'center'">
          <v-col :cols="isMobileBreakpoint ? 12 : 9">
            <v-row no-gutters class="mr-4">
              <span class="body-bold-base activation-card--title">{{
                $t(
                  'message.pages.videoPage.settings.aiWorkflow.activationCard.title'
                )
              }}</span>
            </v-row>
            <v-row no-gutters>
              <span class="body-regular-small activation-card--description">{{
                $t(
                  'message.pages.videoPage.settings.aiWorkflow.activationCard.description'
                )
              }}</span>
            </v-row>
            <v-row no-gutters>
              <base-chip class="activation-card--chip" size="small">
                <span class="body-bold-smaller brand-20-darkest--text">{{
                  $t('message.ai.mindmap.mindmap')
                }}</span>
              </base-chip>
              <base-chip class="activation-card--chip" size="small"
                ><span class="body-bold-smaller brand-20-darkest--text"
                  >Quiz</span
                ></base-chip
              >
              <base-chip class="activation-card--chip" size="small"
                ><span class="body-bold-smaller brand-20-darkest--text">{{
                  $i18next.t('message.ai.abstract.ebook')
                }}</span></base-chip
              >
            </v-row>
          </v-col>
          <v-col
            :cols="isMobileBreakpoint ? 'auto' : 3"
            align-self="center"
            :class="{ 'mt-5': isMobileBreakpoint }"
          >
            <v-row no-gutters justify="end">
              <v-tooltip
                v-model="showConversionTooltip"
                content-class="converting-video-tooltip"
                bottom
              >
                <template v-slot:activator="{ attrs }">
                  <base-btn
                    v-bind="attrs"
                    class="activation-card--action-btn"
                    type="primary"
                    :disabled="isConverting"
                    @click="openAiWorkFlowChargeModal"
                    @mouseenter="handleConversionTooltip($event)"
                    @mouseleave="handleConversionTooltip($event)"
                    >{{
                      $t(
                        'message.pages.videoPage.settings.aiWorkflow.activationCard.action'
                      )
                    }}
                    <v-icon class="ml-2" size="20">panda-go</v-icon>
                  </base-btn>
                </template>
                <span
                  class="body-bold-smaller"
                  v-html="
                    $t(
                      'message.pages.videoPage.details.alert.converting.tooltip'
                    )
                  "
                ></span>
              </v-tooltip>
            </v-row>
          </v-col>
        </v-row>
      </v-sheet>
      <ai-workflow-charge-modal
        :openModal="isModalOpen"
        :aiWorkFlowCreditsBalance="aiWorkFlowCreditsBalance"
        :aiWorkFlowCreated="aiWorkFlowCreated"
      ></ai-workflow-charge-modal>
    </v-col>
  </v-row>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import aiworkflowApi from '@/services/aiworkflowApi'
import creditsInfo from '@/components/CreditsInfo.vue'
import BaseBtn from '@/components/BaseBtn.vue'
import BaseChip from '@/components/BaseComponents/BaseChip.vue'
import BaseAlert from '@/components/BaseAlert.vue'
import BaseProgressCircular from '@/components/BaseComponents/BaseProgressCircular.vue'
import aiWorkflowChargeModal from './AiWorkFlowChargeModal.vue'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import videosApi from '@/services/videosApi.js'

export default {
  name: 'VideoPlayerOptionsAI',
  components: {
    creditsInfo,
    aiWorkflowChargeModal,
    BaseBtn,
    BaseChip,
    BaseProgressCircular,
    BaseAlert,
  },
  props: {
    isBeta: {
      required: true,
    },
    handleSelectOption: {
      type: Function,
      required: true,
    },
    isConverting: {
      type: Boolean,
      required: true,
    },
    canSeePlanInfo: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    config: {},
    isModalOpen: false,
    creditsBalance: 0,
    mindmap: false,
    abstract: false,
    questions: false,
    aiWorkFlowCreditsBalance: {
      aiWorkFlowPrice: 0,
      remainingCredits: 0,
      creditsBalance: 0,
    },
    aiWorkFlowCreated: false,
    isLoading: {
      questions: false,
      abstract: false,
      mindmap: false,
    },
    loopCheck: 0,
    shouldRestartLoop: false,
    isAiWorkFlowLoaded: false,
    fromLang: 'auto',
    isCompleted: false,
    hasError: false,
    originalLang: false,
    queueIds: null,
    mounted: false,
    showConversionTooltip: false,
  }),
  mixins: [formatTimeMixin],

  computed: {
    ...mapState({
      video: (state) => state.video.video,
      hasStatus: (state) => state.video.aiWorkFlow?.status,
      hasData: (state) => state.video.aiWorkFlow?.data,
      isEnabled: (state) => state.video.aiWorkFlow?.isEnabled,
      panda_ai: (state) => state.video.panda_ai,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    isEnabledAndReady() {
      this.mounted
      const isActivationCompleted = !Object.values(this.isLoading).includes(
        true
      )
      return this.isEnabled && isActivationCompleted && !this.hasError
    },
    isItemEnabledOnPlayer() {
      return {
        mindmap: this.mindmap || this.video.config?.ai?.mindmap,
        abstract: this.abstract || this.video.config?.ai?.abstract,
        questions: this.questions || this.video.config?.ai?.questions,
      }
    },
  },
  created() {
    this.aiWorkFlowCreated = !!this.isEnabled
  },
  async mounted() {
    const { status_check_control } = this.panda_ai
    if (this.isEnabled && !status_check_control) this.isActivationCompleted()
    this.$root.$on('closeAiWorkFlowModal', () => (this.isModalOpen = false))
    this.$root.$on('openAiWorkFlowModal', () => (this.isModalOpen = true))

    this.$root.$on('balance', (balance) => {
      this.aiWorkFlowCreditsBalance.creditsBalance = balance
      this.aiWorkFlowPriceCalc()
      this.remainingCreditsCalc()
    })
    this.$root.$on('generateAiWorkFlow', () => {
      this.generateAiWorkFlow()
    })
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.$root.$emit('reloadBalance')
    this.mounted = true
  },
  beforeDestroy() {
    this.$root.$off('generateAiWorkFlow')
    this.$root.$off('closeAiWorkFlowModal')
    this.$root.$off('openAiWorkFlowModal')
    clearInterval(this.updateInterval)
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
  },
  watch: {
    isLoading: {
      handler(val) {
        if (Object.values(val).includes(false)) this.fetchAiWorkFlowData()
        this.$root.$emit('fetchSubtitles')
      },
      deep: true,
    },
    isEnabledAndReady: {
      handler(val) {
        this.$emit('isEnabledAndReady', val)
        if (val) this.getVideoConfig()
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations('video', [
      'UPDATE_AI_WORKFLOW_DATA',
      'UPDATE_AI_WORKFLOW_STATUS',
      'UPDATE_AI_WORKFLOW',
      'UPDATE_PANDA_AI',
    ]),
    ...mapMutations('video', ['UPDATE_VIDEO_CONFIG']),
    ...mapActions('video', ['aiWorkFlowStatus', 'handleFetchAiWorkFlowData']),
    handleConversionTooltip(e) {
      const isHovering = ['mouseenter'].includes(e.type)

      if (isHovering && this.isConverting) {
        this.showConversionTooltip = true
      } else {
        this.showConversionTooltip = false
      }
    },
    async isActivationCompleted() {
      const status = await this.fetchStatus()
      if (!status) {
        this.aiWorkFlowCreated = false
        return
      }
      const statusValues = Object.values(status)

      const isCompleted = this.checkIfAllFinished(
        ['QUESTIONS', 'MINDMAP', 'ABSTRACT'],
        status
      )
      const isError = statusValues.some((value) =>
        ['ERROR', 'SKIPPED'].includes(value.status)
      )
      const isQueueing = statusValues.some((value) =>
        ['QUEUE', 'RUNNING'].includes(value.status)
      )

      if (isCompleted) {
        this.aiWorkFlowCreated = true
        this.isAiWorkFlowLoaded = true
        this.updateAiWorkflowStatusIndexDB(true)
        this.getVideoConfig()
        this.UPDATE_PANDA_AI({ key: 'status_check_control', value: true })
      } else if (isError && !isQueueing) {
        this.hasError = true
      } else {
        this.shouldRestartLoop = true
        this.fetchAiWorkFlowStatus()
      }
    },

    async fetchStatus() {
      try {
        const status = await this.aiWorkFlowStatus(this.video.id)
        return status
      } catch (error) {
        console.error('Error fetching AI Workflow status:', error)
        return false
      }
    },

    async generateAiWorkFlow() {
      try {
        await this.checkIfHasTrancription()

        const { data } = await aiworkflowApi.createAiWorkFlow({
          fromLang: this.fromLang,
          videoId: this.video.id,
        })

        this.queueIds = Object.values(data).map((obj) => obj.id)
        this.hasError = false
        this.$root.$emit('reloadBalance')
        this.aiWorkFlowCreated = true
        this.shouldRestartLoop = true
        this.updateAiWorkflowStatusIndexDB(true)
        this.fetchAiWorkFlowStatus()
      } catch (error) {
        this.aiWorkFlowCreated = false
        console.log(error)
        this.updateAiWorkflowStatusIndexDB(false)
      }
    },
    async fetchAiWorkFlowStatus() {
      try {
        let status = await this.aiWorkFlowStatus(this.video.id)
        if (status) {
          this.handleLoading(status)
        } else {
          this.aiWorkFlowCreated = false
        }
      } catch (error) {
        console.error('Error fetching AI Workflow status:', error)
        this.hasError = true
      }
    },
    handleLoading(status) {
      let statusFromThisGeneration
      if (this.queueIds) {
        statusFromThisGeneration = status.filter((obj) =>
          this.queueIds.includes(obj.id)
        )
      } else {
        statusFromThisGeneration = status
      }

      const isCompleted = this.checkIfAllFinished(
        ['QUESTIONS', 'MINDMAP', 'ABSTRACT'],
        statusFromThisGeneration
      )
      const isQueueing = statusFromThisGeneration.some((value) =>
        ['QUEUE', 'RUNNING'].includes(value.status)
      )

      statusFromThisGeneration.forEach((obj) => {
        const typeLower = obj.type.toLowerCase()
        const isFinished = obj.status === 'FINISHED'
        const isError = obj.status === 'ERROR' || obj.status === 'SKIPPED'

        this.isLoading[typeLower] = !isFinished

        if (isError && !isQueueing && !isCompleted) {
          this.hasError = true
          clearInterval(this.updateInterval)
          this.aiWorkFlowCreated = false
        } else {
          this.aiWorkFlowCreated = true
        }
      })
      if (
        status.some((obj) => obj.status !== 'FINISHED') &&
        this.loopCheck < 1
      ) {
        this.loopCheck++
        this.startUpdateLoop()
      }
      this.isAiWorkFlowLoaded = !Object.values(this.isLoading).includes(true)
    },
    async getVideoConfig() {
      const {
        data: { config },
      } = await videosApi.getAVideo(this.video.id)
      this.UPDATE_VIDEO_CONFIG(config)
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible' && this.shouldRestartLoop) {
        this.fetchAiWorkFlowStatus()
        this.startUpdateLoop()
      } else if (document.visibilityState === 'hidden') {
        clearInterval(this.updateInterval)
        this.updateInterval = null
      }
    },
    startUpdateLoop() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval)
      }

      this.updateInterval = setInterval(async () => {
        if (!this.isAiWorkFlowLoaded) {
          await this.fetchAiWorkFlowStatus()
        } else {
          clearInterval(this.updateInterval)
          this.updateInterval = null
          this.loopCheck = 0
          this.shouldRestartLoop = false
        }
      }, 10000)
    },
    async fetchAiWorkFlowData() {
      let { pullzone_name, video_external_id } = this.video
      try {
        const ret = await this.handleFetchAiWorkFlowData({
          pullzone_name,
          video_external_id,
        })
        return ret
      } catch (error) {
        console.log(error)
      }
    },
    async updateAiWorkflowStatusIndexDB(param) {
      await window.db.videos.get(this.video.id).then((video) => {
        const updatedAiWorkflow = {
          ...video.aiWorkflow,
          isEnabled: param,
        }
        window.db.videos.update(this.video.id, {
          aiWorkflow: updatedAiWorkflow,
        })
        this.UPDATE_AI_WORKFLOW(param)
      })
    },
    async getIndexDbVideoConfig(videoId) {
      const data = await window.db.videos.where({ id: videoId }).first()
      return data
    },
    openAiWorkFlowChargeModal() {
      this.checkOriginalLang()
      this.isModalOpen = true
    },

    aiWorkFlowPriceCalc() {
      let roundPriceUp = null
      const length = this.video.length
      const minutes = Math.floor(length / 60)
      if (minutes < 1) return 1
      const seconds = Math.floor(length % 60)
      roundPriceUp = seconds > 0
      const final_price = roundPriceUp ? minutes + 1 : minutes
      this.aiWorkFlowCreditsBalance.aiWorkFlowPrice = final_price
    },
    remainingCreditsCalc() {
      const remainingCredits =
        Number(this.aiWorkFlowCreditsBalance.creditsBalance) -
        Number(this.aiWorkFlowCreditsBalance.aiWorkFlowPrice)

      this.aiWorkFlowCreditsBalance.remainingCredits = remainingCredits
    },
    checkOriginalLang() {
      this.originalLang = this.video.original_lang
      if (this.originalLang) {
        this.fromLang = this.originalLang
      }
    },
    checkIfAllFinished(types, statusArray) {
      return types.every((type) =>
        statusArray.some(
          (item) => item.type === type && item.status === 'FINISHED'
        )
      )
    },
    async checkIfHasTrancription() {
      const id = this.video.id
      const { subtitles } = await window.db.videos.where({ id: id }).first()
      if (subtitles?.length) {
        this.fromLang = subtitles[0]
      }
    },
    handleCloseErrorAlert() {
      this.aiWorkFlowCreated = false
      this.hasError = false
      this.updateAiWorkflowStatusIndexDB(false)
    },
  },
}
</script>

<style lang="scss" scoped>
.video-player-options {
  .btn {
    justify-content: space-between;
    text-transform: unset;
  }
}

.optionsText {
  font-weight: 400;
  margin: 0 0 0 5px !important;
  letter-spacing: normal;
  padding: 2px 0;
}

.container-list-icon {
  max-width: 24px;
  max-height: 24px;
}
::v-deep .svg-icon-disabled {
  svg {
    opacity: 0.3;
  }
}

.newSectionStyle {
  background-color: #ebedff;
  color: var(--v-primary-base);
  font-weight: 500;
  text-transform: uppercase;
  width: 68px;
  display: flex;
  border-radius: 24px;
}
.newSectionStyle span {
  margin: 0 auto;
}

.active-btn {
  width: 64px;
  height: 30px !important;
  font-size: 14px;
  text-transform: none;
}

.body-label-caption {
  color: varFigma(neutral-60) !important;
  font-family: Rubik;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.66px;
  text-transform: uppercase;
}

.video-page-settings::v-deep {
  .alert-button {
    .base-button.base-button--secondary.base-button--small {
      background-color: varFigma(neutral-100-white);
    }
    .panda.panda-close {
      color: varFigma(neutral-40-light);
    }
  }
}

.video-page-settings {
  .settings-btn {
    justify-content: space-between;
    text-transform: unset;
    border-bottom: 1px solid varFigma(neutral-95-lightest);
    &.v-btn {
      border-radius: 0px;
      width: 100%;
    }
    &--text {
      &.disabled-text-color {
        color: varFigma(neutral-70);
      }
      color: varFigma(neutral-30-base);
      font-family: Rubik;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      margin: 0 !important;
    }
    &--icon {
      &.disabled-text-color {
        color: varFigma(neutral-70);
      }
      color: varFigma(neutral-20-dark);
    }
    &--loading-chip {
      background: varFigma(brand-90) !important;
      &::v-deep .v-chip__content {
        color: varFigma(brand-20-darkest) !important;
        font-family: Rubik;
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
      }
    }
    &--disabled {
      pointer-events: none;
    }
    &.v-btn::before {
      background-color: inherit !important;
    }
    &:hover {
      background-color: varFigma(neutral-99) !important;
      border-radius: 4px !important;
    }
  }
  &--loader::v-deep {
    .v-skeleton-loader__heading {
      border-radius: 4px !important;
      height: 48px !important;
      width: auto !important;
      gap: 8px !important;
    }
  }
}

.activation-card {
  padding: $spacing-07;
  margin-top: $spacing-05;
  background-color: varFigma(brand-99-lightest);
  border-radius: varFigma(spacing-02);
  border: 1px solid varFigma(neutral-95-lightest);
  &--title {
    color: varFigma(neutral-20-dark);
    padding-bottom: 8px !important;
  }
  &--description {
    color: varFigma(neutral-40-light);
    padding-bottom: 16px !important;
  }
  &--action-btn::v-deep {
    width: auto;
    span {
      display: flex;
    }
  }
  &--chip {
    background: varFigma(brand-90) !important;
    border: none !important;
    margin-right: 8px !important;
    border-radius: $spacing-05 !important;
    padding: 0 $spacing-03 !important;
  }
}

.new-item--chip {
  background: varFigma(brand-90) !important;
  border: none !important;
  margin-left: $spacing-03 !important;
  border-radius: $spacing-05 !important;
  padding: 0 $spacing-03 !important;
}

.brand-20-darkest--text {
  color: varFigma(brand-20-darkest);
}
.enabled-setting-icon {
  border-radius: $spacing-03;
  margin-right: $spacing-03;
  background-color: varFigma(feedback-success-light);
  padding: 0 $spacing-02;
}
</style>
